/**
 * Form Template Storage Utilities
 * Manages saving and loading custom form templates
 */

import { supabase } from '../lib/supabaseClient';
import type { FormTemplate } from '../types/formSchema';
import { DEFAULT_RETROFIT_TEMPLATE } from '../types/formSchema';

const TEMPLATES_TABLE = 'form_templates';

/**
 * Save form template to Supabase (with localStorage fallback)
 */
export async function saveFormTemplate(
  template: FormTemplate, 
  surveyId?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get current user for created_by field (required by RLS policy)
    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase
      .from(TEMPLATES_TABLE)
      .upsert({
        id: template.id,
        name: template.name,
        version: template.version,
        template_data: template,
        survey_id: surveyId || null,
        created_by: user?.id, // Set creator for RLS policy
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'id',
      });

    if (error) {
      console.error('[FormTemplate] Error saving template to Supabase:', error);
      
      // Fallback to localStorage if table doesn't exist OR RLS policy fails
      const shouldFallbackToLocal = 
        error.code === 'PGRST205' || 
        error.message.includes('Could not find the table') ||
        error.code === '42501' || // RLS policy violation
        error.message.includes('row-level security');
      
      if (shouldFallbackToLocal) {
        if (error.code === '42501' || error.message.includes('row-level security')) {
          console.warn('[FormTemplate] ⚠️ RLS policy issue, using localStorage fallback');
          console.warn('[FormTemplate] This may happen if you\'re not authenticated or policies need adjustment');
        } else {
          console.warn('[FormTemplate] ⚠️ Supabase table not found, using localStorage fallback');
        }
        
        try {
          // If this is a survey-specific template, use survey-specific key
          if (surveyId) {
            const localKey = `form_template_${surveyId}`;
            localStorage.setItem(localKey, JSON.stringify(template));
            console.log('[FormTemplate] ✅ Survey template saved to localStorage:', surveyId);
          } else {
            // For non-survey templates, use the old format
            const existingTemplates = JSON.parse(localStorage.getItem('form_templates') || '{}');
            existingTemplates[template.id] = template;
            localStorage.setItem('form_templates', JSON.stringify(existingTemplates));
            console.log('[FormTemplate] ✅ Template saved to localStorage:', template.id);
          }
          return { success: true };
        } catch (localError) {
          return { success: false, error: `Supabase: ${error.message}, localStorage: ${String(localError)}` };
        }
      }
      
      return { success: false, error: error.message };
    }

    console.log('[FormTemplate] ✅ Template saved successfully to Supabase:', template.id);
    return { success: true };
  } catch (error) {
    console.error('[FormTemplate] Exception saving template:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Load form template from Supabase (with localStorage fallback)
 */
export async function loadFormTemplate(templateId: string): Promise<{ template: FormTemplate | null; error?: string }> {
  try {
    // Check if we're requesting the default template
    if (templateId === DEFAULT_RETROFIT_TEMPLATE.id) {
      return { template: DEFAULT_RETROFIT_TEMPLATE };
    }

    const { data, error } = await supabase
      .from(TEMPLATES_TABLE)
      .select('template_data')
      .eq('id', templateId)
      .single();

    if (error) {
      console.error('[FormTemplate] Error loading template from Supabase:', error);
      
      // Fallback to localStorage if table doesn't exist
      if (error.code === 'PGRST205' || error.message.includes('Could not find the table')) {
        console.warn('[FormTemplate] ⚠️ Supabase table not found, checking localStorage');
        try {
          const existingTemplates = JSON.parse(localStorage.getItem('form_templates') || '{}');
          const localTemplate = existingTemplates[templateId];
          if (localTemplate) {
            console.log('[FormTemplate] ✅ Template loaded from localStorage:', templateId);
            return { template: localTemplate as FormTemplate };
          }
        } catch (localError) {
          console.error('[FormTemplate] Error loading from localStorage:', localError);
        }
      }
      
      return { template: null, error: error.message };
    }

    if (!data) {
      console.warn('[FormTemplate] Template not found:', templateId);
      return { template: null, error: 'Template not found' };
    }

    console.log('[FormTemplate] ✅ Template loaded successfully from Supabase:', templateId);
    return { template: data.template_data as FormTemplate };
  } catch (error) {
    console.error('[FormTemplate] Exception loading template:', error);
    return { template: null, error: String(error) };
  }
}

/**
 * List all form templates
 */
export async function listFormTemplates(): Promise<{ templates: Array<{ id: string; name: string; version: string; updatedAt: string }>; error?: string }> {
  try {
    const { data, error } = await supabase
      .from(TEMPLATES_TABLE)
      .select('id, name, version, updated_at')
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('[FormTemplate] Error listing templates:', error);
      return { templates: [], error: error.message };
    }

    // Always include the default template
    const templates = [
      {
        id: DEFAULT_RETROFIT_TEMPLATE.id,
        name: DEFAULT_RETROFIT_TEMPLATE.name,
        version: DEFAULT_RETROFIT_TEMPLATE.version,
        updatedAt: DEFAULT_RETROFIT_TEMPLATE.updatedAt,
      },
      ...(data || []).map((t: any) => ({
        id: t.id,
        name: t.name,
        version: t.version,
        updatedAt: t.updated_at,
      })),
    ];

    return { templates };
  } catch (error) {
    console.error('[FormTemplate] Exception listing templates:', error);
    return { templates: [], error: String(error) };
  }
}

/**
 * Delete form template
 */
export async function deleteFormTemplate(templateId: string): Promise<{ success: boolean; error?: string }> {
  try {
    // Prevent deleting the default template
    if (templateId === DEFAULT_RETROFIT_TEMPLATE.id) {
      return { success: false, error: 'Cannot delete default template' };
    }

    const { error } = await supabase
      .from(TEMPLATES_TABLE)
      .delete()
      .eq('id', templateId);

    if (error) {
      console.error('[FormTemplate] Error deleting template:', error);
      return { success: false, error: error.message };
    }

    console.log('[FormTemplate] ✅ Template deleted successfully:', templateId);
    return { success: true };
  } catch (error) {
    console.error('[FormTemplate] Exception deleting template:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Get or create a survey-specific template
 * This ensures each survey has its own isolated template
 */
export async function getOrCreateSurveyTemplate(
  surveyId: string
): Promise<{ template: FormTemplate | null; error?: string }> {
  try {
    // First, try to load existing survey-specific template
    const { data, error: queryError } = await supabase
      .from(TEMPLATES_TABLE)
      .select('template_data')
      .eq('survey_id', surveyId)
      .single();

    if (data && !queryError) {
      console.log('[FormTemplate] ✅ Loaded existing survey template:', surveyId);
      return { template: data.template_data as FormTemplate };
    }

    // If no template exists (or table doesn't exist), check localStorage
    if (queryError?.code === 'PGRST205' || queryError?.message.includes('Could not find the table')) {
      console.warn('[FormTemplate] ⚠️ Supabase table not found, checking localStorage');
      try {
        const localKey = `form_template_${surveyId}`;
        const localTemplate = localStorage.getItem(localKey);
        if (localTemplate) {
          console.log('[FormTemplate] ✅ Loaded survey template from localStorage:', surveyId);
          return { template: JSON.parse(localTemplate) as FormTemplate };
        }
      } catch (localError) {
        console.error('[FormTemplate] Error loading from localStorage:', localError);
      }
    }

    // No existing template found - clone the default for this survey
    console.log('[FormTemplate] 📋 Creating new survey-specific template from default');
    const newTemplate: FormTemplate = {
      ...DEFAULT_RETROFIT_TEMPLATE,
      id: `survey-${surveyId}-${Date.now()}`,
      name: `${DEFAULT_RETROFIT_TEMPLATE.name} (Survey ${surveyId.substring(0, 8)})`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Save the new survey-specific template
    const { success, error: saveError } = await saveFormTemplate(newTemplate, surveyId);
    
    // If Supabase save fails, save to localStorage
    if (!success && (saveError?.includes('PGRST205') || saveError?.includes('Could not find the table'))) {
      try {
        const localKey = `form_template_${surveyId}`;
        localStorage.setItem(localKey, JSON.stringify(newTemplate));
        console.log('[FormTemplate] ✅ Created survey template in localStorage:', surveyId);
      } catch (localError) {
        return { template: null, error: String(localError) };
      }
    } else if (!success) {
      return { template: null, error: saveError };
    }

    console.log('[FormTemplate] ✅ Created new survey-specific template:', newTemplate.id);
    return { template: newTemplate };
  } catch (error) {
    console.error('[FormTemplate] Exception getting/creating survey template:', error);
    return { template: null, error: String(error) };
  }
}

/**
 * Clone form template
 */
export async function cloneFormTemplate(
  sourceTemplateId: string,
  newName: string
): Promise<{ template: FormTemplate | null; error?: string }> {
  try {
    // Load source template
    const { template: sourceTemplate, error: loadError } = await loadFormTemplate(sourceTemplateId);
    if (loadError || !sourceTemplate) {
      return { template: null, error: loadError || 'Source template not found' };
    }

    // Create new template with new ID and name
    const newTemplate: FormTemplate = {
      ...sourceTemplate,
      id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: newName,
      version: '1.0.0',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Save new template
    const { success, error: saveError } = await saveFormTemplate(newTemplate);
    if (!success) {
      return { template: null, error: saveError };
    }

    return { template: newTemplate };
  } catch (error) {
    console.error('[FormTemplate] Exception cloning template:', error);
    return { template: null, error: String(error) };
  }
}
